import { useCallback, useEffect, useRef, useState } from "react";
import { accountProvisioningService } from "../services/accountProvisioningService";
import { authService, formatAuthErrorMessage } from "../services/authService";
import { ReadinessState } from './accountReadinessPresentation';
import { useAppBootstrap } from "./AppBootstrapProvider";

const defaultServices = {
  get accountProvisioningService() { return accountProvisioningService; },
  get authService() { return authService; }
};

export function useAccountReadinessViewModel(services = defaultServices) {
  const { accountProvisioningService, authService } = services;

  const { currentUser } = useAppBootstrap();
  const uid = currentUser?.uid || null;
  const generation = useRef(0);
  const invalidatePendingRead = useCallback(() => { generation.current += 1; }, []);
  const [state, setState] = useState<ReadinessState>({ uid: null, status: "checking", displayName: "" });

  const refresh = useCallback(async () => {
    const request = ++generation.current;
    if (!uid) {
      setState({ uid: null, status: "complete", displayName: "" });
      return;
    }
    setState((previous) => ({ uid, status: "checking", displayName: previous.uid === uid ? previous.displayName : "" }));
    try {
      // Firebase announces a session before the signup transaction finishes.
      await authService.whenAuthenticationSettled();
      if (request !== generation.current || authService.getCurrentUser()?.uid !== uid) return;
      const result = await accountProvisioningService.getAccountReadiness(uid);
      if (request !== generation.current || authService.getCurrentUser()?.uid !== uid) return;
      setState({ uid, status: result.complete ? "complete" : "incomplete", displayName: result.displayName, birthDate: result.birthDate });
    } catch (error) {
      if (request === generation.current && authService.getCurrentUser()?.uid === uid) {
        setState({
          uid, status: "error", displayName: "", errorMessage: formatAuthErrorMessage(
            error, "emailSignUp", "Could not load your account. Check your connection and try again."
          )
        });
      }
    }
  }, [accountProvisioningService, authService, uid]);

  useEffect(() => {
    void refresh();
    const unsubscribe = accountProvisioningService.onAccountProvisioned((provisionedUid) => {
      if (provisionedUid === uid) void refresh();
    });
    return () => { invalidatePendingRead(); unsubscribe(); };
  }, [uid, refresh, invalidatePendingRead, accountProvisioningService]);

  const current = state.uid === uid ? state : { uid, status: "checking" as const, displayName: "" };
  return { refresh, current };
}
